/**
 * Consume a stashed free-tool claim after sign-up (OVE-4).
 *
 * Mounted at App level (App.tsx) so it survives the onboarding redirect a
 * brand-new signup takes (Layout -> /onboarding), which would unmount a
 * Layout-scoped hook mid-claim. Once bootstrap has produced an activeTeamId, it
 * POSTs /api/review/:token/claim to re-parent the anonymous session's project +
 * issues into the new team, then surfaces the outcome — success, expired, taken,
 * or an unexpected failure — as a toast. Nothing is swallowed silently.
 *
 * Fires once per signed-in session (firedRef). A terminal outcome clears the
 * stash; a transient failure leaves it so the next app load retries, and the 1h
 * TTL in freeScanClaim bounds a stash nobody ever consumes.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAppSession } from './useAppSession';
import { useApiMutation } from './useApiMutation';
import { clientReviewsApi, type ClaimFreeScanResult } from '../lib/api/clientReviews';
import { ApiError } from '../lib/api/apiError';
import { readFreeScanClaim, clearFreeScanClaim } from '../lib/freeScanClaim';
import { log } from '@/lib/logger';

interface ClaimVars {
  reviewToken: string;
  teamId: string;
}

export function useFreeScanClaim(): void {
  const { data: session } = useAppSession();
  const queryClient = useQueryClient();
  const firedRef = useRef(false);

  const claim = useApiMutation<ClaimFreeScanResult, ClaimVars>({
    mutationFn: (token, { reviewToken, teamId }) =>
      clientReviewsApi.claim(token, reviewToken, { teamId }),
  });
  // mutate is identity-stable across renders in TanStack Query, so pinning it
  // keeps the effect deps stable (the mutation object itself is recreated).
  const claimMutate = claim.mutate;

  const teamId = session?.activeTeamId;

  useEffect(() => {
    if (firedRef.current) return;
    if (!teamId) return; // wait until bootstrap created the account + team

    const stash = readFreeScanClaim();
    if (!stash) return; // nothing to claim (absent or expired)

    firedRef.current = true;
    log.info('[useFreeScanClaim] claiming free scan', { teamId, source: stash.source });

    claimMutate(
      { reviewToken: stash.token, teamId },
      {
        onSuccess: (result) => {
          log.info('[useFreeScanClaim] claim succeeded', {
            projectId: result.projectId,
            ticketCount: result.ticketCount,
            alreadyClaimed: result.alreadyClaimed,
          });
          clearFreeScanClaim();
          // Surface the newly-attached project and its issues immediately.
          queryClient.invalidateQueries({ queryKey: ['projects'] });
          queryClient.invalidateQueries({ queryKey: ['app-session'] });
          if (!result.alreadyClaimed) {
            toast.success('Your free scan is saved to your account.');
          }
        },
        onError: (err) => {
          const status = err instanceof ApiError ? err.status : undefined;
          const code = err instanceof ApiError ? err.code : undefined;
          // Observable, not silent: log the real status + code so the failure is
          // queryable instead of a swallowed no-op.
          log.warn('[useFreeScanClaim] claim failed', { status, code, message: err.message });

          const expired = status === 410 || code === 'expired';
          const claimedByOther = code === 'claimed_by_other';
          // The server authorizes the claim on the reviewerId cookie it minted
          // while the visitor was annotating, so a link opened in a different
          // browser is refused. Terminal on purpose: retrying from THIS browser
          // can never produce a cookie it does not have, so leaving the stash
          // would re-fire the refusal on every app load forever.
          const notAuthor = code === 'not_author';
          const terminal =
            expired ||
            claimedByOther ||
            notAuthor ||
            code === 'not_ephemeral' ||
            code === 'not_claimable' ||
            code === 'invalid_team' ||
            status === 404;

          if (terminal) {
            clearFreeScanClaim();
            if (expired) {
              toast.error('That free scan link expired. Run a new scan to save your work.');
            } else if (claimedByOther) {
              toast.error('That scan is already saved to another account.');
            } else if (notAuthor) {
              toast.error('That scan can only be saved by the browser that created it. Your account is ready; run a new scan to save your work.');
            } else {
              toast.error('We could not save your scan. Your account is ready; run the scan again.');
            }
          } else {
            // Transient (network / 5xx / account still settling): leave the stash
            // so the next app load retries, and re-arm this session's guard.
            firedRef.current = false;
          }
        },
      },
    );
  }, [teamId, claimMutate, queryClient]);
}
